import * as yup from 'yup';
import { FileType, FILE_TYPES } from '../constants/file-types.constant';
import { FILE_TYPE_CONFIG } from '../utils/file-type-config';

// Validate fixed-width configuration JSON
const validateFixedWidthConfig = (
  configString: string | undefined,
): boolean => {
  if (!configString || configString.trim().length === 0) {
    return false;
  }

  try {
    const parsed = JSON.parse(configString);

    // Must be an array
    if (!Array.isArray(parsed)) {
      return false;
    }

    // Each item must have name, start_pos, and length keys with correct types
    const allValid = parsed.every(
      item =>
        typeof item === 'object' &&
        item !== null &&
        'name' in item &&
        typeof item.name === 'string' &&
        item.name.trim().length > 0 &&
        'start_pos' in item &&
        'length' in item &&
        typeof item.start_pos === 'number' &&
        typeof item.length === 'number',
    );

    if (!allValid) {
      return false;
    }

    // Check for duplicate field names
    const names = parsed.map(item => item.name);
    const uniqueNames = new Set(names);
    return names.length === uniqueNames.size;
  } catch {
    return false;
  }
};

export const providerFormSchema = yup.object({
  // Required base fields
  configName: yup.string().trim().required('Config name is required'),

  providerName: yup.string().trim().required('Provider name is required'),

  fileNamePattern: yup
    .string()
    .trim()
    .required('File name pattern is required'),

  contentType: yup.string().trim().required('Content type is required'),

  fileType: yup
    .string()
    .trim()
    .required('File type is required')
    .oneOf(
      Object.values(FILE_TYPES),
      'Invalid file type',
    ) as yup.StringSchema<FileType>,

  permittedUse: yup.string().trim().required('Permitted use is required'),

  lineOfBusiness: yup
    .array()
    .of(yup.string())
    .min(1, 'At least one line of business is required')
    .required('Line of business is required'),

  // File processing fields
  fileFormat: yup.string().required('File format is required'),
  separator: yup.string().required('Field separator is required'),
  hasHeader: yup.boolean().required('Header configuration is required'),

  // Cadence fields (optional)
  cadenceTimeframe: yup.string().optional(),
  cadenceDay: yup.string().when('cadenceTimeframe', {
    is: (val: string) => ['Bi-Weekly', 'Weekly'].includes(val),
    then: schema => schema.optional(),
    otherwise: schema => schema.optional(),
  }),

  // Conditional validation based on fileType
  clientColumn: yup.string().when('fileType', {
    is: (fileType: FileType) =>
      FILE_TYPE_CONFIG[fileType]?.clientColumnRequired,
    then: schema => schema.trim().required('Client column is required'),
    otherwise: schema => schema.trim().optional(),
  }),

  clientIdentifier: yup.string().when('fileType', {
    is: (fileType: FileType) =>
      FILE_TYPE_CONFIG[fileType]?.clientIdentifierRequired,
    then: schema => schema.trim().required('Client identifier is required'),
    otherwise: schema => schema.trim().optional(),
  }),

  insurerIdentifier: yup.string().when('fileType', {
    is: (fileType: FileType) =>
      FILE_TYPE_CONFIG[fileType]?.insurerIdentifierRequired,
    then: schema => schema.trim().required('Insurer identifier is required'),
    otherwise: schema => schema.trim().optional(),
  }),

  // Fixed width validation
  fixedWidthConfig: yup.string().when('fileFormat', {
    is: 'fixed_width_txt',
    then: schema =>
      schema
        .required('Fixed width configuration is required')
        .test(
          'valid-json',
          'Invalid JSON: Must be an array where each object has "name", "start_pos", and "length" keys',
          validateFixedWidthConfig,
        ),
    otherwise: schema => schema.optional(),
  }),
});

export type ProviderFormSchema = yup.InferType<typeof providerFormSchema>;
